import { useState, useEffect, useCallback } from 'react';
import {
  collection,
  addDoc,
  deleteDoc,
  doc,
  getDocs,
  serverTimestamp,
  Timestamp,
} from 'firebase/firestore';
import { db } from './firebase';
import { useAuth } from './AuthContext';

const useVisits = (resortId) => {
  const { user } = useAuth();
  const [visits, setVisits] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchVisits = useCallback(async () => {
    if (!user) {
      setVisits([]);
      setLoading(false);
      return;
    }

    try {
      const visitsRef = collection(db, 'users', user.uid, 'visits');
      const snapshot = await getDocs(visitsRef);
      let results = snapshot.docs.map(d => ({ id: d.id, ...d.data() }));

      if (resortId) {
        results = results.filter(v => v.resortId === resortId);
      }

      // Sort by date descending
      results.sort((a, b) => {
        const dateA = a.date?.toDate ? a.date.toDate() : new Date(a.date);
        const dateB = b.date?.toDate ? b.date.toDate() : new Date(b.date);
        return dateB - dateA;
      });

      setVisits(results);
    } catch (error) {
      console.error('Error fetching visits:', error);
    } finally {
      setLoading(false);
    }
  }, [user, resortId]);

  useEffect(() => {
    fetchVisits();
  }, [fetchVisits]);

  const addVisit = useCallback(async ({
    resortId: rId,
    resortName,
    date,
    snowRating,
    conditions,
    notes,
    source,
    stravaActivityId,
  }) => {
    if (!user) return;

    const visitsRef = collection(db, 'users', user.uid, 'visits');
    const payload = {
      resortId: rId,
      resortName,
      date: Timestamp.fromDate(new Date(date)),
      snowRating: snowRating ?? null,
      conditions: conditions || [],
      notes: notes || '',
      createdAt: serverTimestamp(),
    };
    if (source) payload.source = source;
    if (stravaActivityId) payload.stravaActivityId = stravaActivityId;

    await addDoc(visitsRef, payload);
    await fetchVisits();
  }, [user, fetchVisits]);

  const removeVisit = useCallback(async (visitId) => {
    if (!user) return;
    await deleteDoc(doc(db, 'users', user.uid, 'visits', visitId));
    setVisits(prev => prev.filter(v => v.id !== visitId));
  }, [user]);

  // Returns the set of stravaActivityIds already logged as visits, so callers
  // can skip duplicates before writing.
  const getImportedStravaIds = useCallback(async () => {
    if (!user) return new Set();
    const visitsRef = collection(db, 'users', user.uid, 'visits');
    const snapshot = await getDocs(visitsRef);
    const ids = new Set();
    snapshot.docs.forEach(d => {
      const data = d.data();
      if (data.stravaActivityId) ids.add(String(data.stravaActivityId));
    });
    return ids;
  }, [user]);

  return { visits, loading, addVisit, removeVisit, getImportedStravaIds };
};

export default useVisits;
